import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addComment,
  addRelation,
  addTaskLabel,
  deleteComment,
  deleteCommentAttachment,
  deleteRelation,
  getRelations,
  getTimeline,
  listSubtasks,
  removeTaskLabel,
  updateComment,
  type CommentAttachment,
  type TaskRelationType,
} from '../lib/tasksApi';

// Invalidate everything that can change when a comment / relation / sub-issue
// is mutated, so the detail panel and the parent list stay in sync.
function invalidateTask(qc: ReturnType<typeof useQueryClient>, taskId: string) {
  void qc.invalidateQueries({ queryKey: ['task', taskId] });
  void qc.invalidateQueries({ queryKey: ['task-timeline', taskId] });
  void qc.invalidateQueries({ queryKey: ['task-relations', taskId] });
  void qc.invalidateQueries({ queryKey: ['task-subtasks', taskId] });
  void qc.invalidateQueries({ queryKey: ['tasks'] });
}

export function useTimeline(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-timeline', taskId],
    queryFn: () => getTimeline(taskId as string),
    enabled: Boolean(taskId),
  });
}

export function useSubtasks(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-subtasks', taskId],
    queryFn: () => listSubtasks(taskId as string),
    enabled: Boolean(taskId),
  });
}

export function useRelations(taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-relations', taskId],
    queryFn: () => getRelations(taskId as string),
    enabled: Boolean(taskId),
  });
}

export function useAddComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; attachments?: CommentAttachment[] }) =>
      addComment(taskId, input.content, input.attachments),
    onSuccess: () => invalidateTask(qc, taskId),
  });
}

export function useUpdateComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      commentId: string;
      content?: string;
      attachments?: CommentAttachment[] | null;
    }) =>
      updateComment(taskId, input.commentId, {
        content: input.content,
        attachments: input.attachments,
      }),
    onSuccess: () => invalidateTask(qc, taskId),
  });
}

export function useDeleteComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(taskId, commentId),
    onSuccess: () => invalidateTask(qc, taskId),
  });
}

export function useDeleteCommentAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { commentId: string; index: number; name: string }) =>
      deleteCommentAttachment(taskId, v.commentId, v.index, v.name),
    onSuccess: () => invalidateTask(qc, taskId),
  });
}

// CLE-183/184: attach / detach a label on a task. Both return the updated task.
export function useAddTaskLabel(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) => addTaskLabel(taskId, labelId),
    onSuccess: () => {
      invalidateTask(qc, taskId);
      void qc.invalidateQueries({ queryKey: ['labels'] });
    },
  });
}

export function useRemoveTaskLabel(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) => removeTaskLabel(taskId, labelId),
    onSuccess: () => {
      invalidateTask(qc, taskId);
      void qc.invalidateQueries({ queryKey: ['labels'] });
    },
  });
}

export function useAddRelation(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toTaskId: string; type: TaskRelationType }) =>
      addRelation(taskId, input.toTaskId, input.type),
    onSuccess: () => invalidateTask(qc, taskId),
  });
}

export function useDeleteRelation(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (relationId: string) => deleteRelation(taskId, relationId),
    onSuccess: () => invalidateTask(qc, taskId),
  });
}
